/**
 * Native bridge — single source of truth for "are we inside the iOS
 * shell or a regular browser?". Everything that touches a native API
 * (push tokens, haptics, share sheet) routes through here so the web
 * build never tries to call iOS-only code.
 *
 * Capacitor 7 lazy-loads the native plugins; on the web they no-op
 * cleanly, so importing them here is safe in both targets.
 */
import { Capacitor } from '@capacitor/core';
import { loadStoredToken } from './nativeSession';
export const isNativeApp = () => Capacitor.isNativePlatform();
// iter307 · The canonical public origin the app should ADVERTISE to
// the outside world (share URLs, magic links, Stripe return URLs,
// og:url canonicals, etc.). Inside the Capacitor iOS/Android shell
// `window.location.origin` is `capacitor://localhost`, which is
// obviously not a real URL — pasting it into iMessage/Safari/an
// email does nothing. This helper returns the production origin
// (`REACT_APP_PUBLIC_ORIGIN` if set, else derived from the API
// backend URL, else the current window origin) so every "copy link"
// and "send to Stripe" flow works identically on web and native.
//
// Only override in unit tests — everywhere else callers should
// consume the result directly.
export const getPublicOrigin = () => {
// Explicit env override wins (set at build time for the iOS bundle).
const override = process.env.REACT_APP_PUBLIC_ORIGIN;
if (override) return override.replace(/\/+$/, '');
// Native shell: use the API host to derive a proper https origin.
if (isNativeApp()) {
const be = process.env.REACT_APP_BACKEND_URL;
if (be) return be.replace(/\/+$/, '');
return 'https://flythouse.com';
}
// Web: whatever the browser says.
if (typeof window !== 'undefined' && window.location && window.location.origin) {
const o = window.location.origin;
// Belt-and-suspenders: if we're somehow inside a webview that still
// reports `capacitor://localhost` even though isNativeApp() was
// false, fall back to the API host — never advertise capacitor://.
if (o.startsWith('capacitor://') || o.startsWith('ionic://')) {
const be = process.env.REACT_APP_BACKEND_URL;
return (be || 'https://flythouse.com').replace(/\/+$/, '');
}
return o;
}
return 'https://flythouse.com';
};
export const platform = () => Capacitor.getPlatform();
/**
 * iter517 · Personalized FLYT ID™ URL builder — now supports THREE modes
 * in priority order:
 *
 *   1. Vanity apex host  (iter553):  `flyt.co/willie`
 *      Set `REACT_APP_VANITY_APEX_HOST=flyt.co`. Cleanest brand
 *      primitive — no `/l/` prefix, one path segment, easy to say
 *      out loud ("my flyt is flyt.co slash willie").
 *
 *   2. Wildcard subdomain:            `willie.flythouse.com`
 *      Set `REACT_APP_FLYTID_WILDCARD_HOST=flythouse.com`. Requires
 *      wildcard DNS + SSL, more infra work.
 *
 *   3. Legacy path-style (default):   `flythouse.com/l/willie`
 *      Always works. Fallback when neither env var is set.
 *
 * The vanity apex wins over wildcard so we can flip to `flyt.co` the
 * moment the second domain is linked at Emergent — one deploy, every
 * "Copy link" + social share + og:url starts emitting the new format.
 *
 * Callers must NEVER concatenate `/l/{slug}` themselves; always go
 * through this helper.
 */
export const buildFlytIdUrl = (slug) => {
if (!slug) return getPublicOrigin();
const apex = process.env.REACT_APP_VANITY_APEX_HOST;
if (apex) {
const root = String(apex).replace(/^https?:\/\//i, '').replace(/\/+$/, '');
return `https://${root}/${slug}`;
}
const wildcard = process.env.REACT_APP_FLYTID_WILDCARD_HOST;
if (wildcard) {
const root = String(wildcard).replace(/^https?:\/\//i, '').replace(/\/+$/, '');
return `https://${slug}.${root}`;
}
return `${getPublicOrigin()}/l/${slug}`;
};
/**
 * iter688p · Shareable UPLOAD-LINK url. ALWAYS uses the OG-capable backend
 * route on the primary host (`flythouse.com/api/l/{slug}`).
 *
 * Why not the vanity `flytid.co/u/{slug}` apex (iter688n)? That path is
 * SPA-routed, so a link-preview crawler (iMessage / WhatsApp / Notes)
 * only ever gets the generic static `index.html` OpenGraph tags — the
 * shared card showed the FLYT logo instead of the athlete's photo. The
 * `/api/l/` route serves per-link OG (athlete photo + "{name} invited
 * you…") and 302-redirects a human to the real `/upload/{token}` flow on
 * the same host, so the contributor upload runs over the real web where
 * it works reliably. Both requirements — athlete cover photo AND a
 * working web link — are only satisfied here.
 */
export const buildUploadLinkUrl = (slugOrToken) => {
if (!slugOrToken) return getPublicOrigin();
return `${getPublicOrigin()}/api/l/${slugOrToken}`;
};
/**
 * iter553 · When the SPA is served from the vanity apex host
 * (`flyt.co`), a single-path-segment URL (`flyt.co/willie`) means
 * "show Willie's FLYT ID™ share page" — the same content the legacy
 * `/l/willie` route renders on `flythouse.com`.
 *
 * Returns the slug to render, or null if we're on the primary host
 * (flythouse.com) or if the path matches a reserved system route
 * (admin, profile, network, login, etc.) that must NOT be treated as
 * an athlete slug.
 */
const RESERVED_PATHS = new Set([
'', 'admin', 'api', 'app', 'assets', 'auth', 'billing', 'blocked',
'brand', 'brands', 'checkout', 'contact', 'crm', 'dashboard',
'delete', 'demo', 'error', 'favicon.ico', 'featured', 'flyt',
'for-agents', 'for-brands', 'for-programs', 'founder', 'help',
'home', 'index.html', 'invite', 'l', 'landing', 'legal', 'login',
'logout', 'me', 'network', 'not-found', 'notifications', 'onboarding',
'partnerships', 'password', 'pay', 'pitches', 'press', 'pricing',
'privacy', 'profile', 'program', 'programs', 'pulse', 'recruiter',
'register', 'reports', 'reset', 'reviewer-assets', 'robots.txt',
'safety', 'saved', 'search', 'settings', 'share', 'signout',
'signup', 'sitemap.xml', 'sports', 'stories', 'story', 'stripe',
'subscription', 'support', 'sw.js', 'terms', 'test', 'thanks',
'trending', 'upgrade', 'verify', 'video', 'videos', 'welcome',
'why-flyt', 'widget_preview.html', 'u', 'upload',
]);
export const getVanityApexSlug = () => {
const apex = process.env.REACT_APP_VANITY_APEX_HOST;
if (!apex || typeof window === 'undefined' || !window.location) return null;
const currentHost = window.location.hostname.toLowerCase();
const apexHost = String(apex).replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();
if (currentHost !== apexHost && currentHost !== `www.${apexHost}`) return null;
const seg = window.location.pathname.replace(/^\/+/, '').split('/')[0] || '';
if (!seg || RESERVED_PATHS.has(seg.toLowerCase())) return null;
// Slugs are lowercase alphanumeric + hyphens (matches how they're
// minted from Notion) — anything with unusual chars is not a slug.
if (!/^[a-z0-9][a-z0-9-]{0,63}$/i.test(seg)) return null;
return seg;
};
/**
 * iter517 · If the current page is loaded on a personalized FLYT ID™
 * subdomain (e.g. `jaxon.flythouse.com`), return the slug portion so
 * the SPA can silently mount the /l/{slug} route. Returns null on the
 * apex domain or any reserved subdomain (www, app, api, admin, ...).
 *
 * Reserved list is intentionally small + defensive — the backend has
 * the authoritative list; this is only used client-side to route.
 */
const RESERVED_SUBDOMAINS = new Set([
'www', 'app', 'api', 'admin', 'support', 'help', 'mail', 'blog',
'auth', 'cdn', 'assets', 'static', 'dev', 'stage', 'staging',
'preview', 'test', 'internal', 'status', 'docs', 'ftp', 'smtp',
'ns1', 'ns2', 'email', 'go',
]);
export const readFlytIdFromHost = () => {
const wildcard = process.env.REACT_APP_FLYTID_WILDCARD_HOST;
if (!wildcard || typeof window === 'undefined' || !window.location) return null;
const root = String(wildcard).replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();
const host = String(window.location.hostname || '').toLowerCase();
if (!host.endsWith(`.${root}`)) return null;
const sub = host.slice(0, -1 * (root.length + 1)); // strip ".<root>"
if (!sub) return null;
// Only single-label subdomains are athlete slugs. Multi-label
// subdomains (e.g. "www.marketing") are not FLYT IDs.
if (sub.includes('.')) return null;
if (RESERVED_SUBDOMAINS.has(sub)) return null;
// Slug shape: lowercase letters/numbers/hyphens, 2-40 chars.
if (!/^[a-z0-9][a-z0-9-]{1,39}$/.test(sub)) return null;
return sub;
};
/** Shared: wire the APNs registration listener + register. */
async function attachAndRegister(api, PushNotifications) {
  PushNotifications.addListener('registration', async (t) => {
try {
// iter798 · CRITICAL — on iOS the session is a Bearer token held in
// Capacitor Preferences, NOT a cookie (the WebView runs from
// capacitor://localhost, so `credentials: 'include'` sends nothing
// the backend can authenticate). These two calls used to fire with
// cookies only, so the backend saw no user and stored EVERY token
// with user_id=null — orphaned forever, which is why the new-photos
// scheduler (`if not uid: continue`) skipped every device and no
// native push ever went out. Attach the Bearer token the same way
// apiClient.js does for every other authed call. `await` the load
// so a cold-boot registration can't race ahead of the token being
// read into memory.
const authToken = await loadStoredToken();
const headers = { 'Content-Type': 'application/json' };
if (authToken) headers.Authorization = `Bearer ${authToken}`;
await fetch(`${api}/push/native-token`, {
method: 'POST',
headers,
credentials: 'include',
body: JSON.stringify({ token: t.value, platform: platform() }),
});
// iter752 · web push already has this exact fix (see webPush.js) for
// the exact same race: if this registration event fires before the
// session cookie is actually attached — app launch, or push
// permission granted during onboarding before login finishes — the
// token lands server-side with user_id=null and NOTHING ever picks
// it back up again, since nothing else calls claim-orphans for the
// native path. That silently and permanently excludes the device
// from every server-triggered push (new-photos alerts included) —
// this is very likely why real accounts report never receiving a
// single native push despite permission showing "granted". Cheap +
// idempotent, so safe to fire on every registration, not just once.
await fetch(`${api}/push/claim-orphans`, {
method: 'POST',
headers,
credentials: 'include',
});
} catch { /* fail open — UI badge still works */ }
});
// iter806 · Deep-link on tap — every server push carries a top-level
// `url` (see apns_sender.py: message["url"]); route the in-app SPA
// there when the athlete taps the banner, instead of just opening to
// whatever screen was last open. Mirrors the web-push tap handler in
// App.js (history.pushState + popstate). Fires for BOTH the new-photos
// push and the new contributor-video push.
PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
  try {
    const url = action?.notification?.data?.url;
    if (url && typeof url === 'string' && url.startsWith('/')) {
      window.history.pushState({}, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  } catch { /* noop */ }
});
await PushNotifications.register();
}
/**
 * Current native push permission state without prompting.
 * Returns 'unsupported' | 'prompt' | 'prompt-with-rationale' |
 * 'granted' | 'denied'.
 */
export async function getNativePushStatus() {
if (!isNativeApp()) return 'unsupported';
try {
const { PushNotifications } = await import('@capacitor/push-notifications');
const s = await PushNotifications.checkPermissions();
return s.receive || 'unsupported';
} catch { return 'unsupported'; }
}
/**
 * SILENT boot-time registration — refreshes the APNs token only when
 * permission was already granted. Never triggers the iOS permission
 * dialog; that moment belongs to <NativePushPrimer> so users see the
 * value pitch first. Safe to call on web — becomes a no-op.
 */
export async function registerNativePush(api) {
if (!isNativeApp()) return;
try {
const { PushNotifications } = await import('@capacitor/push-notifications');
const perm = await PushNotifications.checkPermissions();
if (perm.receive !== 'granted') return;
await attachAndRegister(api, PushNotifications);
} catch { /* plugin not available — ignore */ }
}
/**
 * EXPLICIT permission request — fires the real iOS dialog. Call only
 * from a user gesture (the primer's "Turn on" button). Returns true
 * when granted + registered.
 */
export async function requestNativePush(api) {
if (!isNativeApp()) return false;
try {
const { PushNotifications } = await import('@capacitor/push-notifications');
const perm = await PushNotifications.requestPermissions();
if (perm.receive !== 'granted') return false;
await attachAndRegister(api, PushNotifications);
return true;
} catch { return false; }
}
/**
 * iter481 · Haptic feedback API — thin wrapper around @capacitor/haptics
 * that keeps callers dead simple: `haptic('medium')` for a primary CTA,
 * `haptic('light')` for a tab / chip / secondary tap, `haptic('success')`
 * for a "you did it!" moment, and `haptic('selection')` while scrubbing
 * through a wheel picker. Silent no-op on web + inside a fallback try
 * so a haptics failure never breaks the button that triggered it.
 *
 * Palette:
 *   'light'      → ImpactStyle.Light          — tabs, chips, toggles, tab switches
 *   'medium'     → ImpactStyle.Medium         — primary CTAs (Track Live, Save)
 *   'heavy'      → ImpactStyle.Heavy          — big moment kicker (confetti)
 *   'success'    → NotificationType.Success   — "You're on the network!" chime
 *   'warning'    → NotificationType.Warning   — approve/reject warning
 *   'error'      → NotificationType.Error     — validation fail
 *   'selection'  → Haptics.selectionChanged() — one tick per scroll step
 */
export async function haptic(kind = 'light') {
if (!isNativeApp()) return;
try {
const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
const k = String(kind || 'light').toLowerCase();
if (k === 'selection') {
await Haptics.selectionChanged();
return;
}
if (k === 'success' || k === 'warning' || k === 'error') {
const typeMap = {
success: NotificationType.Success,
warning: NotificationType.Warning,
error: NotificationType.Error,
};
await Haptics.notification({ type: typeMap[k] });
return;
}
const styleMap = {
light: ImpactStyle.Light,
medium: ImpactStyle.Medium,
heavy: ImpactStyle.Heavy,
};
await Haptics.impact({ style: styleMap[k] || ImpactStyle.Light });
} catch { /* silent — never break UX on haptics failure */ }
}
/**
 * Legacy alias — kept for existing call sites. Delegates to
 * `haptic('light')`. New code should call `haptic(...)` directly.
 */
export async function tapHaptic() {
await haptic('light');
}
/**
 * iter650 · Save an image URL to the user's Photos library.
 *
 * Native iOS/Android: fetch the image bytes, drop them into the
 * Capacitor cache directory, then hand the file URI to the system
 * Share sheet. iOS's share sheet includes "Save Image" which triggers
 * the standard "Flythouse would like to add to your Photos" permission
 * prompt on first use — no manual plist entry beyond
 * `NSPhotoLibraryAddUsageDescription` which we already ship. Zero new
 * plugins required.
 *
 * Web fallback: anchor tag with `download` — uses Cloudinary's
 * `fl_attachment` rewrite so the browser actually downloads instead of
 * opening the image in-place.
 */
export async function saveToPhotos(imageUrl, opts = {}) {
const filename = opts.filename || `flyt-${Date.now()}.jpg`;
if (isNativeApp()) {
try {
const [{ Filesystem, Directory }, { Share }] = await Promise.all([
import('@capacitor/filesystem'),
import('@capacitor/share'),
]);
const resp = await fetch(imageUrl);
if (!resp.ok) throw new Error(`fetch ${resp.status}`);
const blob = await resp.blob();
const b64 = await new Promise((resolve, reject) => {
const r = new FileReader();
        r.onloadend = () => resolve(String(r.result).split(',')[1] || '');
        r.onerror = reject;
        r.readAsDataURL(blob);
});
const written = await Filesystem.writeFile({
path: filename,
data: b64,
directory: Directory.Cache,
});
await Share.share({
title: opts.title || 'Save to Photos',
text: opts.text || '',
url: written.uri,
dialogTitle: 'Save to Photos',
});
return { ok: true, method: 'native' };
} catch (e) {
const msg = String(e?.message || e || '');
if (msg.includes('canceled')) return { ok: true, method: 'native-cancel' };
// eslint-disable-next-line no-console
      console.warn('[saveToPhotos] native path failed, falling back to link:', msg);
}
}
try {
const rewritten = imageUrl.includes('/image/upload/')
      ? imageUrl.replace('/image/upload/', '/image/upload/fl_attachment/')
      : imageUrl;
const a = document.createElement('a');
    a.href = rewritten;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
return { ok: true, method: 'web' };
} catch (e) {
return { ok: false, error: String(e?.message || e) };
}
}
/**
 * Confirm to the Capacitor Updater that this bundle launched cleanly.
 * Without this call within ~10s of boot, the updater assumes the
 * bundle is broken and rolls back to the previous one on next launch.
 * Safe to call on web — no-op.
 */
export async function confirmLiveBundle() {
if (!isNativeApp()) return;
try {
const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
await CapacitorUpdater.notifyAppReady();
} catch { /* plugin not present — ignore */ }
}
/**
 * Native iOS share sheet (used by ShareToStoryButton when running in
 * the app — falls back to navigator.share / clipboard on web).
 */
export async function nativeShare(opts) {
if (!isNativeApp()) return false;
try {
const { Share } = await import('@capacitor/share');
await Share.share(opts);
return true;
} catch { return false; }
}
/**
 * openExternalAuth — open an OAuth URL in a way Google will accept.
 *
 * Google Sign-In refuses to authenticate inside a Capacitor / WebView
 * origin ("This browser may not be secure"), so the flow runs in an
 * in-app Safari sheet (SFSafariViewController via `@capacitor/browser`).
 *
 * iter885 · ROOT CAUSE of "spins / never opens Google / Could not open
 * the sign-in browser". Capacitor 7's `presentVC` shows every
 * NON-popover sheet inside a brand-new `UIWindow(frame:)`. Since the app
 * adopted the UIScene lifecycle (2026-09-18, the 1.4.5 rejection fix),
 * a window created that way has no windowScene and is never displayed:
 * the Google sheet is "presented" into an invisible window, Browser.open
 * never resolves, and the plugin then keeps that dead sheet so every
 * later tap is rejected ("Unable to display URL"). `popover` presents
 * straight on the app's real view controller instead (full screen on
 * iPhone), which the scene lifecycle shows normally.
 *
 * Returns 'sheet' (in-app sheet up), 'safari' (handed to the Safari
 * app — the caller keeps waiting for the handoff), or 'redirect' (web).
 * Never throws on iOS unless nothing at all can open the URL.
 */
export async function openExternalAuth(url) {
if (!url) return 'none';
if (isNativeApp()) {
const within = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('sheet timed out')), ms))]);
try {
const { Browser } = await import('@capacitor/browser');
try {
await within(Browser.open({ url, presentationStyle: 'popover' }), 5000);
return 'sheet';
} catch (firstErr) {
// A sheet left over from an earlier attempt blocks a new one
// ("Unable to display URL") — close it and try once more.
console.warn('openExternalAuth: sheet did not open, closing any stale sheet and retrying', firstErr);
await within(Browser.close(), 1500).catch(() => {});
await within(Browser.open({ url, presentationStyle: 'popover' }), 5000);
return 'sheet';
}
} catch (err) {
console.error('openExternalAuth: in-app sheet failed, opening Safari instead', err);
}
// Last resort: the real Safari app. The sign-in still completes —
// the caller keeps claiming /auth/handoff, including when the
// person switches back to FLYT.
try {
const { AppLauncher } = await import('@capacitor/app-launcher');
await AppLauncher.openUrl({ url });
return 'safari';
} catch (err) {
console.error('openExternalAuth: AppLauncher failed', err);
}
try { window.open(url, '_blank'); return 'safari'; } catch { /* nothing left */ }
throw new Error('Could not open Google sign-in. Please try again.');
}
  window.location.href = url;
return 'redirect';
}
/**
 * iter688v · requestNativePhotoAccess — trigger the REAL iOS "Allow Full
 * Access to Photo Library" system dialog before opening a photo picker.
 *
 * `@capacitor-community/media` has no explicit requestPermissions API; it
 * prompts automatically the first time you touch the library, so calling
 * `getAlbums()` once surfaces Apple's native permission sheet (the
 * NSPhotoLibraryUsageDescription string is already in Info.plist). We
 * only need it to fire on native — the web contributor flow uses a
 * normal <input type=file> (PHPicker) which needs no system prompt.
 *
 * Always resolves (never throws) so a denied/limited grant or an old
 * build without the plugin can't block the upload button. Returns true
 * when we could talk to the library, false otherwise. On web: true.
 */
export async function requestNativePhotoAccess() {
if (!isNativeApp()) return true;
try {
const { Media } = await import('@capacitor-community/media');
// Touching the library forces the iOS authorization sheet on first run.
await Media.getAlbums();
return true;
} catch {
return false;
}
}
/**
 * iter818/iter819/iter828 · pickNativeMedia — presents the native iOS
 * picker directly (NativeVideoPickerPlugin.swift), bypassing the plain
 * `<input type=file>` path that has to round-trip the whole file
 * through WebKit's file-upload IPC layer before our JS ever sees it —
 * that round-trip is where the multi-second-to-a-minute-plus delay
 * with zero on-screen feedback was coming from on real iPhone video.
 * Supports picking up to 10 videos at once — Jay: "I don't want it to
 * just be one video." Returns an array of real `File`s (empty on
 * cancel), each ready to hand straight to the existing Mux upload code
 * exactly as if it came from a normal file input. Returns an empty
 * array on web (caller should fall back to the normal file input) or
 * on any native-side failure — never throws, so a missing/older-build
 * plugin can't break the upload flow. A file that fails to read on the
 * native side is just missing from the result, not a hard failure of
 * the whole pick.
 *
 * iter860 · Jay: large/high-res iPhone videos could each take several
 * seconds to export out of PhotosUI, and the native side used to wait
 * for EVERY item to finish that export before resolving `pickMedia()`
 * at all — so a big batch showed nothing, for as long as its slowest
 * file took, after the picker's checkmark was tapped. The plugin now
 * resolves the instant the selection is confirmed, with lightweight
 * placeholders (`{id, isVideo, fileName?}`, no file bytes yet) for
 * every picked item. `onSelected`, if passed, is called with that
 * placeholder list right away — lets a caller render all N picks as
 * "Preparing…" tiles before a single byte has loaded. Each placeholder
 * is then filled in independently via the plugin's `mediaItemReady`
 * event as its own export finishes; this function still materializes
 * their bytes ONE AT A TIME (never concurrently) to avoid the multi-
 * hundred-MB-to-GB memory spike that used to crash the app (iter828) —
 * only the WAIT for that materialization moved off the critical path
 * for showing the selection at all.
 *
 * iter881 · Jay: uploads were freezing forever on "Preparing…" in
 * production — every tile showing, none ever starting. Root cause: an
 * OLD (pre-iter860) native binary resolves `pickMedia()` with FULL
 * items (path already populated) in ONE SHOT and never fires
 * `mediaItemReady` AT ALL — that event doesn't exist on that build.
 * This function used to assume EVERY item arrives via that event, so
 * on an old binary the queue stayed empty forever and `await drained`
 * never resolved — exactly the JS-ahead-of-native-rebuild hazard
 * flagged when iter860 shipped. Two fixes:
 *   1. Any placeholder that ALREADY has a `path` (old-binary shape) is
 *      queued and processed immediately instead of waiting for an
 *      event that build will never send.
 *   2. `onItem` now also fires (with `file: null, error: true`) for a
 *      native-reported per-item failure and for a genuinely-stuck item
 *      once a safety timeout elapses — previously both cases just
 *      called `settle()` with no callback at all, so that ONE tile
 *      spun forever even on an up-to-date binary. Existing callers
 *      that only destructure `(file, id)` are unaffected; callers that
 *      want to show a failed state read the 3rd argument.
 */
export async function pickNativeMedia(onItem, onSelected) {
  if (!isNativeApp()) return [];
  try {
    const plugin = Capacitor.Plugins?.NativeVideoPicker;
    if (!plugin) return [];
    const result = await plugin.pickMedia();
    const placeholders = result?.items || [];
    if (!placeholders.length) return [];
    if (onSelected) { try { onSelected(placeholders); } catch { /* caller's rendering bug shouldn't sink the pick */ } }
    const files = [];
    const queue = [];
    const pendingIds = new Set(placeholders.map((p) => p?.id).filter(Boolean));
    let remaining = placeholders.length;
    let resolveDrained;
    const drained = new Promise((res) => { resolveDrained = res; });
    const settle = (id) => {
      if (id) pendingIds.delete(id);
      remaining -= 1;
      if (remaining <= 0) resolveDrained();
    };
    let draining = false;
    const drainQueue = async () => {
      if (draining) return;
      draining = true;
      while (queue.length) {
        const item = queue.shift();
        if (!item?.error && item?.path) {
          try {
            const url = Capacitor.convertFileSrc(item.path);
            const resp = await fetch(url);
            const blob = await resp.blob();
            const file = new File([blob], item.fileName || (item.isVideo ? 'video.mov' : 'photo.jpg'), {
              type: item.mimeType || blob.type || (item.isVideo ? 'video/quicktime' : 'image/jpeg'),
            });
            files.push(file);
            if (onItem) onItem(file, item.id);
          } catch {
            // One bad file in a multi-select batch shouldn't sink the
            // whole pick — but the caller still needs to know THIS
            // tile failed instead of spinning forever.
            if (onItem) onItem(null, item.id, true);
          }
        } else if (onItem) {
          onItem(null, item.id, true);
        }
        settle(item?.id);
      }
      draining = false;
    };
    const listener = await plugin.addListener('mediaItemReady', (item) => {
      queue.push(item);
      drainQueue();
    });
    // Old-binary compatibility — process anything that already arrived
    // with its path populated (see file header) instead of waiting
    // forever for an event that build will never fire.
    const alreadyReady = placeholders.filter((p) => p?.path || p?.error);
    if (alreadyReady.length) {
      queue.push(...alreadyReady);
      drainQueue();
    }
    // Safety net — never let a single stuck item (native export that
    // truly never finishes) hang the whole pick indefinitely. Whatever
    // hasn't settled by then is reported as failed so its tile can
    // show an error instead of spinning forever.
    const SAFETY_TIMEOUT_MS = 45000;
    const timeoutId = setTimeout(() => {
      if (remaining <= 0) return;
      const stragglers = [...pendingIds];
      pendingIds.clear();
      remaining = 0;
      resolveDrained();
      if (onItem) stragglers.forEach((id) => onItem(null, id, true));
    }, SAFETY_TIMEOUT_MS);
    await drained;
    clearTimeout(timeoutId);
    listener.remove();
    return files;
  } catch (e) {
    console.warn('[flyt] native media picker failed, falling back:', e?.message || e);
    return [];
  }
}
/**
 * iter818 · nativeVideoPickerAvailable — true only when the native
 * PhotosUI picker plugin is actually present in THIS build. Gates the
 * "Add a video (fast)" buttons so they stay hidden on any binary that
 * predates NativeVideoPickerPlugin.swift, and appear automatically the
 * moment a native rebuild ships with it — never a dead button.
 */
export function nativeVideoPickerAvailable() {
  return isNativeApp() && !!Capacitor.Plugins?.NativeVideoPicker;
}
/**
 * openInSystemBrowser — open a URL in the REAL external Safari
 * app (not the in-app SFSafariViewController sheet).
 *
 * Preview / "open this upload link" must run in the user's true browser
 * so the contributor upload flow behaves exactly like a parent tapping
 * the link from iMessage — the web path that works. The in-app Capacitor
 * Browser sheet (used by openExternalLink) shares the app's constrained
 * WebView surface and was rendering blank on TestFlight. `AppLauncher`
 * hands the https URL to iOS, which opens Safari externally.
 *
 * Web: normal new-tab open. Safe everywhere.
 */
export async function openInSystemBrowser(url) {
if (!url) return;
if (isNativeApp()) {
try {
const { AppLauncher } = await import('@capacitor/app-launcher');
await AppLauncher.openUrl({ url });
return;
} catch { /* fall through to the in-app browser as a last resort */ }
try {
const { Browser } = await import('@capacitor/browser');
// iter885 · popover — see openExternalAuth (fullscreen sheets are
// invisible under the UIScene lifecycle).
await Browser.open({ url, presentationStyle: 'popover' });
return;
} catch { /* fall through to hard navigation */ }
}
try {
const w = window.open(url, '_blank', 'noopener,noreferrer');
if (w) return;
} catch { /* popup blocked — fall through */ }
  window.location.href = url;
}
/**
 * openExternalLink — open an arbitrary URL the right way for the
 * current platform.
 *
 * Why this exists: YouTube iframe embeds fail inside Capacitor's WKWebView
 * (origin is `capacitor://localhost`, which YouTube's embed player rejects
 * with "Video player configuration error / Error 153"). Instead of
 * fighting the origin check we deep-link the user out to the native
 * YouTube app when it's installed, falling back to SFSafariViewController
 * so the video always plays. On web it's a plain new-tab open.
 *
 * `deepLink` (optional): a scheme URL like `youtube://` — if provided,
 * we try the native app first, then fall back to `url` in the Safari
 * sheet. Safe on web (no-op deep link, uses `window.open`).
 */
export async function openExternalLink(url, deepLink) {
if (!url) return;
if (isNativeApp()) {
// Try native app deep link first (opens the real YouTube app if the
// user has it installed). AppLauncher.openUrl silently rejects when
// the scheme isn't registered — we then fall through to the Safari
// sheet so the video still plays.
if (deepLink) {
try {
const { AppLauncher } = await import('@capacitor/app-launcher');
const canOpen = await AppLauncher.canOpenUrl({ url: deepLink });
if (canOpen?.value) {
await AppLauncher.openUrl({ url: deepLink });
return;
}
} catch { /* plugin missing / scheme not allowed — fall through */ }
}
try {
const { Browser } = await import('@capacitor/browser');
await Browser.open({ url, presentationStyle: 'popover' });
return;
} catch { /* plugin missing — fall through to hard navigation */ }
}
try {
const w = window.open(url, '_blank', 'noopener,noreferrer');
if (w) return;
} catch { /* popup blocked — fall through */ }
  window.location.href = url;
}
/**
 * Universal-Link listener for iOS. Runs once at app boot.
 *
 * Flow:
 *   1. User taps "Sign in with Google" → we redirect to
 *      auth.emergentagent.com with `?redirect=https://flythouse.com/auth/callback`.
 *   2. Google → Emergent Auth → Safari (or the in-app browser) redirects
 *      to https://flythouse.com/auth/callback#session_id=<token>.
 *   3. Because we ship an apple-app-site-association declaring
 *      /auth/callback as an app link, iOS intercepts the redirect and
 *      hands the URL to our app via `App.addListener('appUrlOpen', ...)`.
 *   4. This handler extracts the path + hash and pushes it into the
 *      SPA history — the existing <AuthCallback> component then fires
 *      because it detects `session_id=` in `location.hash`.
 *
 * Without this bridge, the OAuth redirect lands in Safari and shows
 * "Safari cannot open the page because the address is invalid" — the
 * exact TestFlight bug this fixes.
 *
 * Safe to call on web: no-op.
 */
export async function bindUniversalLinks() {
if (!isNativeApp()) return;
try {
const { App } = await import('@capacitor/app');
    App.addListener('appUrlOpen', async ({ url }) => {
if (!url) return;
try {
let parsed = new URL(url);
        // iter870 · flythouse://payouts/done (the app's own URL scheme,
        // used by the Stripe payout return page's "Open the FLYT app"
        // button) parses with host "payouts" — rewrite it to the same
        // https path a Universal Link would carry.
        if (parsed.protocol === 'flythouse:') {
          parsed = new URL(`https://flythouse.com/${parsed.hostname}${parsed.pathname}${parsed.search}${parsed.hash}`);
        }
// Only handle our own domain. Anything else — punt to the
// default handler (Safari) so users don't get trapped in-app.
const isFlyt = /flythouse\.com$/i.test(parsed.hostname);
if (!isFlyt) return;
// Close the Safari view that OAuth was running inside so the
// user pops back to the app the moment the callback lands.
// Best-effort — no-op if the browser plugin isn't loaded.
import('@capacitor/browser').then(({ Browser }) => {
          Browser.close().catch(() => { /* ignore */ });
}).catch(() => { /* ignore */ });
let target = `${parsed.pathname}${parsed.search || ''}${parsed.hash || ''}`;
// Upload invite links are shared as /api/l/{slug} (or legacy
// /l/{slug}) — resolve those to the real /upload/{token} the
// contributor page expects, so a tapped link lands in-app.
if (/^\/(?:api\/l|l)\//i.test(parsed.pathname)) {
const resolved = await resolveInvitePath(url);
if (resolved) target = resolved;
}
// Push into SPA history so react-router picks it up. If the
// URL carries an OAuth `#session_id=...`, <AuthCallback>
// runs on the next render and completes the sign-in.
        window.history.pushState({}, '', target || '/');
// Nudge react-router: it listens to popstate, not pushState,
// so dispatch a synthetic event.
try { window.dispatchEvent(new PopStateEvent('popstate')); } catch { /* ignore */ }
} catch { /* ignore malformed URL */ }
});
} catch { /* @capacitor/app not present — web fallback path */ }
}
/**
 * checkDeferredInvite()
 * The "smart claim" install-survival hop. When someone taps an upload
 * link in Safari WITHOUT the app, the web page copies that invite URL to
 * the clipboard before sending them to the App Store. On the app's FIRST
 * launch we read the clipboard once, and if it holds a flythouse.com
 * /upload/<token> link we route them straight into that athlete's upload
 * flow — no code to type, mirroring the mockup-athlete claim feel.
 *
 * Runs once per install (guarded by a localStorage flag) so we never
 * nag returning users with the iOS paste prompt. Installed apps opening
 * the link directly go through AASA + appUrlOpen instead. No-op on web.
 */
export async function checkDeferredInvite() {
if (!isNativeApp()) return;
try {
if (localStorage.getItem('flyt_deferred_invite_checked') === '1') return;
    localStorage.setItem('flyt_deferred_invite_checked', '1');
const { Clipboard } = await import('@capacitor/clipboard');
const { value } = await Clipboard.read();
const target = await resolveInvitePath(value);
if (!target) return;
try { await Clipboard.write({ string: '' }); } catch { /* ignore */ }
    window.history.pushState({}, '', target);
try { window.dispatchEvent(new PopStateEvent('popstate')); } catch { /* ignore */ }
} catch { /* clipboard unavailable / denied — silent, no claim */ }
}
/**
 * resolveInvitePath(str)
 * Turns a shared FLYT invite string into the in-app SPA path to open.
 * The athlete-sent link is `https://flythouse.com/api/l/{slug}` (an OG
 * route that 302s a human to `/upload/{token}`). We also accept the
 * legacy `/l/{slug}` and a direct `/upload/{token}`. For the `/api/l/`
 * and `/l/` forms we follow the redirect to recover the real token so
 * the contributor upload page (which expects a token) loads correctly.
 * Returns a path like `/upload/{token}` or null if nothing matches.
 */
export async function resolveInvitePath(str) {
if (!str) return null;
const s = String(str);
// Accept the direct upload link, the OG short link, and the legacy
// short link. The token OR the vanity slug both resolve on the backend
// (validate/signature/register/upload all match token-or-slug), so we
// can route straight to /upload/{value} without following any redirect.
const m = s.match(/flythouse\.com\/(?:api\/l|l|upload)\/([A-Za-z0-9._-]+)/i);
return m ? `/upload/${m[1]}` : null;
}
// ─── Meta (Facebook) App Events ──────────────────────────────────────
// iter100 · Log conversion events to Meta so iOS app-install ads can be
// measured/optimised beyond installs. Native-only — routes to the custom
// `MetaEvents` Capacitor plugin (see MetaEventsPlugin.swift). On the web
// this is a silent no-op. NEVER log purchases here — the Meta SDK auto-
// logs StoreKit purchases/subscriptions (AutoLogAppEvents), so a manual
// purchase event would double-count.
export async function logMetaEvent(eventName, parameters = {}) {
try {
if (!isNativeApp() || !eventName) return;
const plugin = Capacitor?.Plugins?.MetaEvents;
if (!plugin?.logEvent) return;
// Meta App Events only accept string/number param values.
const clean = {};
    Object.entries(parameters || {}).forEach(([k, v]) => {
if (typeof v === 'string' || typeof v === 'number') clean[k] = v;
});
await plugin.logEvent({ eventName, parameters: clean });
} catch {
// Best-effort — analytics must never break a user flow.
}
}
// Standard Meta event name for a completed sign-up.
export const metaCompletedRegistration = (method) =>
logMetaEvent('fb_mobile_complete_registration',
    method ? { fb_registration_method: String(method) } : {});
// Fires the FIRST time an athlete completes/publishes their FLYT ID.
export const metaProfileCompleted = () => logMetaEvent('profile_completed');
// Fires when an athlete creates a new game folder.
export const metaGameCreated = (eventId) =>
logMetaEvent('game_created', eventId ? { game_id: String(eventId) } : {});

// iter · Tells the Media Hub Home-Screen widget which account it
// belongs to (via `FlytWidgetContextPlugin.swift`'s shared App Group
// container) — zero-config, tied to the logged-in athlete, never a
// slug someone typed into "Edit Widget". Call whenever the logged-in
// athlete's own data loads (their dashboard, on login). Also nudges
// WidgetKit to refresh right away, which is free while the app is in
// the foreground (only background refreshes are OS-rate-limited).
export async function setMediaHubWidgetAthlete(slug) {
  try {
    if (!isNativeApp() || !slug) return;
    const plugin = Capacitor?.Plugins?.FlytWidgetContext;
    if (!plugin?.setAthlete) return;
    await plugin.setAthlete({ slug: String(slug).toLowerCase() });
  } catch {
    // Best-effort — the widget just keeps showing whatever it last had.
  }
}

// Call on logout so a shared/handed-down device doesn't keep showing
// the previous account's Media Hub widget.
export async function clearMediaHubWidgetAthlete() {
  try {
    if (!isNativeApp()) return;
    const plugin = Capacitor?.Plugins?.FlytWidgetContext;
    if (!plugin?.clearAthlete) return;
    await plugin.clearAthlete();
  } catch {
    // Best-effort.
  }
}
